import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';
import { MonitoringPreferencesService } from '../../../core/services/monitoring-preferences.service';
import { RequestsService } from '../../../core/services/utils/requests.service';

/** Recreate scoped pages on preference changes, including their local caches and viewers. */
@Component({
  selector: 'app-monitoring-outlet',
  imports: [RouterOutlet, RouterLink, TranslateModule],
  template: `
    @if (!blocked()) {
      @for (key of keys(); track key) { <router-outlet></router-outlet> }
    } @else {
      <section class="selection-state" aria-live="polite">
        <h2>{{'MONITORING_PREFERENCES.TITLE' | translate}}</h2>
        <p>{{message() | translate}}</p>
        @if (preferences.error()) {
          <button type="button" (click)="preferences.refresh()">{{'MONITORING_PREFERENCES.RETRY' | translate}}</button>
        }
        <a [routerLink]="settingsPath()">{{'MONITORING_PREFERENCES.OPEN_SETTINGS' | translate}}</a>
      </section>
    }
  `,
  styles: [`:host { display: block; height: 100%; } .selection-state { margin: 24px; padding: 28px; border-radius: 18px; background: #fff; color: #263247; } h2 { font-size: 1.2rem; } a, button { display: inline-block; margin: 8px 16px 0 0; color: #1764c0; }`]
})
export class MonitoringOutletComponent {
  readonly preferences = inject(MonitoringPreferencesService);
  private readonly router = inject(Router);
  private readonly user = toSignal(inject(RequestsService).currentUser$);
  private readonly url = toSignal(this.router.events.pipe(
    filter(event => event instanceof NavigationEnd), map(() => this.router.url), startWith(this.router.url)
  ), { initialValue: this.router.url });
  private readonly scoped = computed(() => /^\/wita(?:Mobile)?\/(mentorage|monitoring|alertsHistory|analytics|diagnostic|devices|structure)(\/|\?|#|$)/.test(this.url()));
  private readonly required = computed(() => /^\/wita(?:Mobile)?\/(mentorage|monitoring|alertsHistory|analytics)(\/|\?|#|$)/.test(this.url()));
  private readonly globalAnalytics = computed(() => this.user()?.role === 'superuser'
    && /^\/wita(?:Mobile)?\/analytics(\/|\?|#|$)/.test(this.url()));
  readonly keys = computed(() => [this.scoped() ? this.preferences.revision() : -1]);
  readonly blocked = computed(() => !this.globalAnalytics() && this.required() && this.preferences.selection()?.state !== 'READY');
  readonly settingsPath = computed(() => this.url().startsWith('/witaMobile') ? '/witaMobile/settings' : '/wita/settings');
  readonly message = computed(() => {
    this.preferences.revision();
    return 'MONITORING_PREFERENCES.' + (this.preferences.error() ? 'LOAD_ERROR'
    : !this.preferences.activeBuildingId ? 'CHOOSE_BUILDING'
    : !this.preferences.selection() ? 'LOADING'
    : this.preferences.selection()?.state === 'NO_ACCESSIBLE_UNITS' ? 'NO_UNITS' : 'REQUIRED');
  });
}
